import * as React from "react"
import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { ImageZoom } from "@/components/ui/image-zoom"

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

export interface CarouselItem {
  id: string
  imageUrl: string
  title?: string
  description?: string
  alt?: string
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
  opts?: CarouselOptions
  orientation?: "horizontal" | "vertical"
}

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />")
  }

  return context
}

interface CarouselProps extends React.HTMLAttributes<HTMLDivElement> {
  items: CarouselItem[]
  onItemClick?: (item: CarouselItem, index: number) => void
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: "horizontal" | "vertical"
  setApi?: (api: CarouselApi) => void
}

const Carousel = React.forwardRef<HTMLDivElement, CarouselProps>(
  (
    {
      items,
      onItemClick,
      orientation = "horizontal",
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === "horizontal" ? "x" : "y",
      },
      plugins
    )
    const [canScrollPrev, setCanScrollPrev] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return
      }

      setCanScrollPrev(api.canScrollPrev())
      setCanScrollNext(api.canScrollNext())
    }, [])

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev()
    }, [api])

    const scrollNext = React.useCallback(() => {
      api?.scrollNext()
    }, [api])

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault()
          scrollPrev()
        } else if (event.key === "ArrowRight") {
          event.preventDefault()
          scrollNext()
        }
      },
      [scrollPrev, scrollNext]
    )

    React.useEffect(() => {
      if (!api || !setApi) {
        return
      }

      setApi(api)
    }, [api, setApi])

    React.useEffect(() => {
      if (!api) {
        return
      }

      onSelect(api)
      api.on("reInit", onSelect)
      api.on("select", onSelect)

      return () => {
        api?.off("select", onSelect)
      }
    }, [api, onSelect])

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn("relative", className)}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          <CarouselContent items={items} onItemClick={onItemClick} />
          <CarouselPrevious />
          <CarouselNext />
        </div>
      </CarouselContext.Provider>
    )
  }
)
Carousel.displayName = "Carousel"

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    items: CarouselItem[]
    onItemClick?: (item: CarouselItem, index: number) => void
  }
>(({ className, items, onItemClick, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div ref={carouselRef} className="overflow-hidden w-full">
      <div
        ref={ref}
        className={cn(
          "flex",
          orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
          className
        )}
        {...props}
      >
        {items.map((item, index) => (
          <CarouselItem
            key={item.id}
            item={item}
            index={index}
            totalItems={items.length}
            onItemClick={onItemClick}
          />
        ))}
      </div>
    </div>
  )
})
CarouselContent.displayName = "CarouselContent"

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    item: CarouselItem
    index: number
    totalItems: number
    onItemClick?: (item: CarouselItem, index: number) => void
  }
>(({ className, item, index, totalItems, onItemClick, ...props }, ref) => {
  const { orientation } = useCarousel()

  const handleClick = () => {
    onItemClick?.(item, index)
  }

  return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      aria-label={`${index + 1} of ${totalItems}`}
      className={cn(
        "min-w-0 shrink-0 grow-0",
        // Show 3 items on desktop, 2 on tablet, 1.5 on mobile for better fit
        "basis-1/3",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className
      )}
      {...props}
    >
      <div className="relative rounded-2xl overflow-hidden bg-white/70 backdrop-blur-sm border-2 border-white/80 shadow-lg shadow-cyan-100/30 transition-all hover:shadow-xl hover:shadow-cyan-200/40 hover:scale-[1.02]">
        <div className="aspect-[4/3] w-full overflow-hidden">
          <ImageZoom
            src={item.imageUrl}
            alt={item.alt || item.title || `Image ${index + 1}`}
            className="w-full h-full object-cover transition-transform hover:scale-110 duration-300"
            loading={index < 3 ? "eager" : "lazy"}
            onClick={handleClick}
          />
        </div>
        {(item.title || item.description) && (
          <div className="p-3 space-y-1 bg-gradient-to-t from-white/90 to-white/60">
            {item.title && (
              <h3 className="font-semibold text-sm text-gray-800 line-clamp-1">
                {item.title}
              </h3>
            )}
            {item.description && (
              <p className="text-xs text-gray-600 line-clamp-2">
                {item.description}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
})
CarouselItem.displayName = "CarouselItem"

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, variant = "outline", size = "icon", ...props }, ref) => {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel()

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        "absolute h-9 w-9 rounded-full bg-white/90 backdrop-blur-sm border-2 border-white/80 shadow-lg shadow-cyan-100/40 transition-all hover:bg-white hover:shadow-cyan-200/50 hover:scale-110 text-gray-700",
        orientation === "horizontal"
          ? "-left-12 top-1/2 -translate-y-1/2"
          : "-top-12 left-1/2 -translate-x-1/2 rotate-90",
        !canScrollPrev && "opacity-0 pointer-events-none",
        // On mobile, position inside the carousel
        "lg:-left-12 left-2",
        className
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ChevronLeft className="h-5 w-5" />
      <span className="sr-only">Previous slide</span>
    </Button>
  )
})
CarouselPrevious.displayName = "CarouselPrevious"

const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, variant = "outline", size = "icon", ...props }, ref) => {
  const { orientation, scrollNext, canScrollNext } = useCarousel()

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        "absolute h-9 w-9 rounded-full bg-white/90 backdrop-blur-sm border-2 border-white/80 shadow-lg shadow-cyan-100/40 transition-all hover:bg-white hover:shadow-cyan-200/50 hover:scale-110 text-gray-700",
        orientation === "horizontal"
          ? "-right-12 top-1/2 -translate-y-1/2"
          : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
        !canScrollNext && "opacity-0 pointer-events-none",
        // On mobile, position inside the carousel
        "lg:-right-12 right-2",
        className
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ChevronRight className="h-5 w-5" />
      <span className="sr-only">Next slide</span>
    </Button>
  )
})
CarouselNext.displayName = "CarouselNext"

export { Carousel, type CarouselApi }
